/**
 * Plan sequence service — placements + dependency-order check (WS-C).
 *
 * PRD: docs/9-exploration/PRD-scorecard-to-strongest.md.
 * Schema + RPC: supabase/migrations/682_plan_sequencing_and_signal_links.sql.
 *
 * Placement is a human act via the SECURITY DEFINER RPC (§2.3a); re-placing
 * auto-supersedes the prior placement in a visible chain (L3). The sequence
 * check is flag-only: it names the inversion, a human resolves it by
 * re-placing (L2).
 */

import { getTypedSupabaseClient } from './typedSupabaseClient';
import type { Database } from '../types/database.generated';

export type PlanItemPlacementRow =
  Database['public']['Tables']['plan_item_placements']['Row'];
export type PlanSequenceCheckRow =
  Database['public']['Views']['plan_sequence_check']['Row'];

export interface PlacementWriteResult {
  ok: boolean;
  placementId?: string;
  error?: string;
}

export async function placePlanItem(input: {
  planCycleId: string;
  itemId: string;
  periodIndex: number;
  periodLabel?: string;
  basis?: string;
}): Promise<PlacementWriteResult> {
  const db = getTypedSupabaseClient();
  const { data, error } = await db.rpc('place_plan_item', {
    p_plan_cycle_id: input.planCycleId,
    p_item_id: input.itemId,
    p_period_index: input.periodIndex,
    p_period_label: input.periodLabel ?? undefined,
    p_basis: input.basis ?? undefined,
  });
  if (error) return { ok: false, error: error.message };
  return { ok: true, placementId: typeof data === 'string' ? data : undefined };
}

export async function listPlacementsForCycle(
  planCycleId: string
): Promise<{ rows: PlanItemPlacementRow[]; error?: string }> {
  const db = getTypedSupabaseClient();
  const { data, error } = await db
    .from('plan_item_placements')
    .select('*')
    .eq('plan_cycle_id', planCycleId)
    .eq('status', 'placed')
    .order('period_index', { ascending: true });
  if (error) return { rows: [], error: error.message };
  return { rows: data ?? [] };
}

export async function listSequenceCheckForCycle(
  planCycleId: string
): Promise<{ rows: PlanSequenceCheckRow[]; error?: string }> {
  const db = getTypedSupabaseClient();
  const { data, error } = await db
    .from('plan_sequence_check')
    .select('*')
    .eq('plan_cycle_id', planCycleId);
  if (error) return { rows: [], error: error.message };
  return { rows: data ?? [] };
}
